<template>
	<div class="flex justify-center">
		<canvas
			ref="qrCanvas"
			class="rounded-lg border border-gray-200"
		></canvas>
	</div>
</template>

<script setup lang="ts">
	import { onMounted, ref, watch } from "vue";
	import QRCode from "qrcode";

	interface Props {
		url: string;
		size?: number;
		showLogo?: boolean;
		logoSize?: number; // Percentage as decimal (0.2 = 20%)
	}

	const props = withDefaults(defineProps<Props>(), {
		size: 300,
		showLogo: true,
		logoSize: 0.2,
	});

	const qrCanvas = ref<HTMLCanvasElement | null>(null);

	const generateQRCode = async () => {
		if (!qrCanvas.value) return;

		// Generate QR code with high error correction to allow logo overlay
		await QRCode.toCanvas(qrCanvas.value, props.url, {
			width: props.size,
			margin: 2,
			errorCorrectionLevel: "H",
			color: {
				dark: "#000000",
				light: "#FFFFFF",
			},
		});

		// Add logo in the center if enabled
		if (props.showLogo) {
			const ctx = qrCanvas.value.getContext("2d");
			if (ctx) {
				const logo = new Image();
				logo.src = "/favicon.png";
				logo.onload = () => {
					// Calculate logo size based on percentage
					const calculatedLogoSize = props.size * props.logoSize;
					const x = (props.size - calculatedLogoSize) / 2;
					const y = (props.size - calculatedLogoSize) / 2;

					// Draw white background square for better visibility
					const padding = 4;
					const bgSize = calculatedLogoSize + padding * 2;
					const bgX = (props.size - bgSize) / 2;
					const bgY = (props.size - bgSize) / 2;

					ctx.fillStyle = "white";
					ctx.fillRect(bgX, bgY, bgSize, bgSize);

					// Draw the logo
					ctx.drawImage(
						logo,
						x,
						y,
						calculatedLogoSize,
						calculatedLogoSize,
					);
				};
			}
		}
	};

	onMounted(() => {
		generateQRCode();
	});

	// Regenerate QR code if any prop changes
	watch(props, generateQRCode);
</script>
